import React, { useCallback, memo, ChangeEvent, useState } from 'react';
import * as Styled from '../styled';
import {
  SelectOption,
  StyledText,
} from '@leasequery/lq.react-component-library';
import { ReportCurrency } from '../../../../models';
import { ReportSettingReportDateInput } from '../ReportSettingReportDateInput';
import { Icon } from '../../../../components';
import { useAuth } from '../../../../context';
import { 
  getSingleDateComponentFormatConfig,
  isValidDateByFormat,
  parseLocaleSpecificDateStringToDefaultFormat
} from '../../../../util/dateHelpers';
import { DateRangeMode } from '../date-range/DateRange';

interface ReportSettingCustomProps {
  onChange: (updatedValue: ReportSettingCustomState) => void;
  reportSettingState: ReportSettingCustomState;
}

export interface ReportSettingCustomState {
  selectedCurrency: SelectOption<ReportCurrency>;
  isValid: boolean;
  reportDate: string;
}

function ReportDateSettingCustom({
  onChange,
  reportSettingState,
}: ReportSettingCustomProps): JSX.Element {
  const [dateError, setDateError] = useState('');
  const [isDateValid, setIsDateValid] = useState(true);

  const { user } = useAuth();
  const localePreference = user.localePreference;

  const {
    dateFormat,
    placeHolder,
    inputMask,
    formattedDate,
  } = getSingleDateComponentFormatConfig(
    localePreference,
    DateRangeMode.FullDate,
    reportSettingState.reportDate,
    isDateValid,
  );

  const reportDateHandler = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;

      const isValid = isValidDateByFormat(value, dateFormat);
      setIsDateValid(isValid);

      const errorMessage = isValid ? '' : `Please enter a valid date (${dateFormat.toUpperCase()}).`;
      setDateError(!value ? '' : errorMessage);

      const defaultFormatValue =
        isValid
          ? parseLocaleSpecificDateStringToDefaultFormat(value, localePreference)
          : value;

      onChange({
        ...reportSettingState,
        reportDate: defaultFormatValue,
        isValid: isValid,
      });
    },
    [dateFormat, localePreference, onChange, reportSettingState],
  );

  return (
    <>
      <Styled.ReportDateRow>
        <div>
          <Icon name="calendar" />
        </div>
        <div>
          <StyledText as="div" fontWeight="medium">
            Report Date
          </StyledText>
          <StyledText as="div" className="reportDateDescription">
            The Report Date indicates which month/period of information the
            report will reflect.
            <br />
            For example, if you are running a report with the current month cash
            field, this date should reflect the month/period from which you want
            the cash to be reported.
          </StyledText>
          <ReportSettingReportDateInput
            name="reportDate"
            className="shortDate"
            placeholder={placeHolder}
            mask={inputMask}
            value={formattedDate}
            error={dateError}
            onChange={reportDateHandler}
          />
        </div>
      </Styled.ReportDateRow>
    </>
  );
}

export default memo(ReportDateSettingCustom);
